import { FlashcardsService } from "@/client"
import { AppEmptyState } from "@/components/Common/AppEmptyState"
import { HtmlContent } from "@/components/Common/HtmlContent"
import { FlashcardContent } from "@/components/Flashcards/FlashcardContent"
import { PageShell, PageSkeleton } from "@/components/Layout"
import {
  PaginationItems,
  PaginationNextTrigger,
  PaginationPrevTrigger,
  PaginationRoot,
} from "@/components/ui/pagination"
import { useRouteBreadcrumbs } from "@/navigation/route-meta"
import { formatDateTime } from "@/utils/format-date"
import { Badge, Box, HStack, Stack, Text, VStack } from "@chakra-ui/react"
import { useQuery } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { useState } from "react"

const PER_PAGE = 50

type DetailSearch = {
  programId?: string
}

export const Route = createFileRoute("/_layout/flashcards/notes/$studentId")({
  validateSearch: (search: Record<string, unknown>): DetailSearch => ({
    programId:
      typeof search.programId === "string" ? search.programId : undefined,
  }),
  component: FlashcardStudentNoteDetailPage,
})

function FlashcardStudentNoteDetailPage() {
  const { studentId } = Route.useParams()
  const { programId } = Route.useSearch()
  const [page, setPage] = useState(1)
  const breadcrumbs = useRouteBreadcrumbs("flashcards.index", [
    { label: "Student Notes", href: "/flashcards/notes" },
    { label: "Student Detail" },
  ])

  const { data, isLoading } = useQuery({
    queryKey: ["flashcards-admin-notes", studentId, programId, page],
    queryFn: () =>
      FlashcardsService.getProgramStudentNotes({
        studentId,
        programId: programId as string,
        offset: (page - 1) * PER_PAGE,
        limit: PER_PAGE,
      }),
    enabled: Boolean(programId),
  })

  if (!programId) {
    return (
      <PageShell title="Student Notes" breadcrumbs={breadcrumbs}>
        <AppEmptyState
          title="Program required"
          description="Open this page from the student notes list for a selected program."
        />
      </PageShell>
    )
  }

  if (isLoading) {
    return (
      <PageShell
        title="Student Notes"
        description="Read-only saved flashcards and notes for the selected program."
        breadcrumbs={breadcrumbs}
      >
        <PageSkeleton variant="table" rows={4} />
      </PageShell>
    )
  }

  const savedCards = data?.data ?? []

  return (
    <PageShell
      title="Student Notes"
      description="Read-only saved flashcards and notes for the selected program."
      breadcrumbs={breadcrumbs}
      py={{ base: 5, md: 8 }}
      contentGap={5}
    >
      {savedCards.length === 0 ? (
        <AppEmptyState
          title="No saved flashcards for this student"
          description="This student has not saved any cards in the selected program."
          variant="compact"
        />
      ) : (
        <VStack align="stretch" gap={4}>
          {savedCards.map((savedCard) => (
            <Box
              key={savedCard.id}
              borderWidth={1}
              borderColor="gray.200"
              _dark={{ borderColor: "gray.700", bg: "gray.900" }}
              borderRadius="lg"
              bg="white"
              p={{ base: 4, md: 5 }}
            >
              <Stack gap={4}>
                <Stack gap={2}>
                  <Badge colorPalette="teal" w="fit-content">
                    {savedCard.deck_name}
                  </Badge>
                  <Text fontSize="sm" color="fg.muted">
                    Updated {formatDateTime(savedCard.updated_at)}
                  </Text>
                </Stack>

                <Box>
                  <Text fontSize="xs" fontWeight="bold" mb={2} color="fg.muted">
                    Front
                  </Text>
                  <FlashcardContent content={savedCard.front} />
                </Box>

                <Box>
                  <Text fontSize="xs" fontWeight="bold" mb={2} color="fg.muted">
                    Back
                  </Text>
                  <FlashcardContent content={savedCard.back} />
                </Box>

                <Box
                  borderWidth={1}
                  borderColor="gray.200"
                  _dark={{ borderColor: "gray.700", bg: "gray.800" }}
                  borderRadius="md"
                  bg="gray.50"
                  p={3}
                >
                  <Text fontSize="xs" fontWeight="bold" mb={2} color="fg.muted">
                    Note
                  </Text>
                  {savedCard.note_html ? (
                    <HtmlContent content={savedCard.note_html} />
                  ) : (
                    <Text color="fg.muted">No note added.</Text>
                  )}
                </Box>
              </Stack>
            </Box>
          ))}
        </VStack>
      )}

      {(data?.count ?? 0) > PER_PAGE && (
        <PaginationRoot
          count={data?.count ?? 0}
          pageSize={PER_PAGE}
          page={page}
          onPageChange={(e) => setPage(e.page)}
        >
          <HStack justify="center" gap={2}>
            <PaginationPrevTrigger />
            <PaginationItems />
            <PaginationNextTrigger />
          </HStack>
        </PaginationRoot>
      )}
    </PageShell>
  )
}

export default FlashcardStudentNoteDetailPage
